import React from 'react';

import { Group, Select, SimpleGrid, Stack, Text } from '@mantine/core';
import type { PlotlyTypes } from 'visyn_core/plotly';
import { PlotlyComponent } from 'visyn_core/plotly';

import CountPercentageToggle from './CountPercentageToggle';
import { getDataForFamilyGraph, getTitleForFamilyGraph } from './repertoire-analysis-utils';
import { FamilyGraphRawData } from '../generated/generatedOpenAPI';
import { DisplayType, FamilyOptions } from '../types/experiment';

interface VJGeneUsageProps {
  selectedFamilyOption: FamilyOptions;
  setSelectedFamilyOption: React.Dispatch<React.SetStateAction<FamilyOptions>>;
  familyGraphRawData: FamilyGraphRawData | undefined;
  selectedDisplayType: DisplayType;
  setSelectedDisplayType: React.Dispatch<React.SetStateAction<DisplayType>>;
}

export default function VJGeneUsage({
  selectedFamilyOption,
  setSelectedFamilyOption,
  familyGraphRawData,
  selectedDisplayType,
  setSelectedDisplayType,
}: VJGeneUsageProps) {
  const { familyVGenesPlotData, familyJGenesPlotData } = React.useMemo(() => {
    const title = getTitleForFamilyGraph(selectedFamilyOption);
    if (!familyGraphRawData) {
      return {
        familyVGenesPlotData: {
          title,
          data: [],
        },
        familyJGenesPlotData: {
          title,
          data: [],
        },
      };
    }
    return {
      familyVGenesPlotData: {
        title,
        data: getDataForFamilyGraph(
          familyGraphRawData.vGene,
          selectedFamilyOption,
          { VH: '#337ab7', VK: '#f75a1e', VL: '#15a154' },
          selectedDisplayType === DisplayType.PERCENTAGE,
        ),
      },
      familyJGenesPlotData: {
        title,
        data: getDataForFamilyGraph(
          familyGraphRawData.jGene,
          selectedFamilyOption,
          { VH: '#337ab7', VK: '#f75a1e', VL: '#15a154' },
          selectedDisplayType === DisplayType.PERCENTAGE,
        ),
      },
    };
  }, [familyGraphRawData, selectedFamilyOption, selectedDisplayType]);

  const yAxisLabel: string = selectedDisplayType === DisplayType.PERCENTAGE ? 'Percentage of cells' : 'Cells';

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text>Gene Distribution</Text>
        <Group gap={7}>
          <Text>Chain type:</Text>
          <Select
            data-testid="vj-gene-usage-select"
            value={selectedFamilyOption}
            onChange={(value) => {
              if (typeof value === 'string') {
                setSelectedFamilyOption(value as FamilyOptions);
              }
            }}
            data={[
              { label: FamilyOptions.VH, value: FamilyOptions.VH },
              { label: FamilyOptions.VK, value: FamilyOptions.VK },
              { label: FamilyOptions.VL, value: FamilyOptions.VL },
            ]}
            w={100}
          />
          <CountPercentageToggle displayType={selectedDisplayType} setDisplayType={setSelectedDisplayType} />
        </Group>
        {/* <Group gap={7}>
        </Group> */}
      </Group>
      <SimpleGrid cols={2} data-testid="vj-gene-usage" spacing="md">
        <PlotlyComponent
          data={familyVGenesPlotData.data as PlotlyTypes.Data[]}
          style={{ width: '100%' }}
          layout={{
            height: 350,
            margin: {
              t: 30,
              b: 50,
            },
            xaxis: {
              title: {
                text: 'V gene family',
                font: { size: 16 },
              },
              automargin: true,
            },
            yaxis: {
              title: {
                text: yAxisLabel,
                font: { size: 16 },
              },
            },
            title: {
              text: familyVGenesPlotData.title,
              font: { size: 16 },
            },
          }}
          config={{
            displaylogo: false,
            modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'autoScale2d', 'select2d'],
          }}
        />
        <PlotlyComponent
          data={familyJGenesPlotData.data as PlotlyTypes.Data[]}
          useResizeHandler
          style={{ width: '100%' }}
          layout={{
            height: 350,
            margin: {
              t: 30,
              b: 50,
            },
            xaxis: {
              title: {
                text: 'J gene family',
                font: { size: 16 },
              },
            },
            yaxis: {
              title: {
                text: yAxisLabel,
                font: { size: 16 },
              },
            },
            title: {
              text: familyJGenesPlotData.title,
              font: { size: 16 },
            },
          }}
          config={{
            displaylogo: false,
            modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'autoScale2d', 'select2d'],
          }}
        />
      </SimpleGrid>
    </Stack>
  );
}
